/**
 * @Mention Autocomplete Component
 * 
 * Instagram/Discord-style @mention dropdown that appears when typing @
 * Shows available agents with avatars, names, and online status
 */

import React, { useState, useEffect, useRef } from 'react'
import { User, CheckCircle, Circle } from 'lucide-react'
import { cn } from '../../utils/cn'
import { logger } from '../../utils/logger'
import type { Agent } from '../../hooks/useMentionAutocomplete'

export interface MentionAutocompleteProps {
  /** Show/hide the dropdown */
  isVisible: boolean
  /** Available agents to mention */
  agents: Agent[]
  /** Current search query (text after @) */
  searchQuery: string
  /** Position relative to textarea */
  position: { top: number; left: number }
  /** Callback when agent is selected */
  onSelect: (agent: Agent) => void
  /** Callback when dropdown should close */
  onClose: () => void
  /** Currently highlighted index for keyboard navigation */
  highlightedIndex: number
  /** Loading state */
  isLoading?: boolean
}

export function MentionAutocomplete({
  isVisible,
  agents,
  searchQuery,
  position,
  onSelect,
  onClose,
  highlightedIndex,
  isLoading = false
}: MentionAutocompleteProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // Debug logging
  useEffect(() => {
    logger.info('🎯 MentionAutocomplete state changed', {
      isVisible,
      agentCount: agents.length,
      searchQuery,
      position,
      highlightedIndex,
      isLoading
    })
  }, [isVisible, agents.length, searchQuery, position, highlightedIndex, isLoading])

  // Filter agents based on search query
  const filteredAgents = agents.filter(agent => 
    agent.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisible, onClose])

  if (!isVisible) {
    logger.debug('🎯 MentionAutocomplete not visible - returning null')
    return null
  }

  logger.info('🎯 MentionAutocomplete rendering', {
    filteredAgentsCount: filteredAgents.length,
    position,
    isLoading
  })

  return (
    <div
      ref={dropdownRef}
      className="absolute z-[9999] w-72 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-60 overflow-y-auto"
      style={{
        top: position.top,
        left: position.left,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Mention Team Member
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="px-3 py-4 text-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
          <div className="text-xs text-gray-500 mt-2">Loading agents...</div>
        </div>
      )}

      {/* No results */}
      {!isLoading && filteredAgents.length === 0 && (
        <div className="px-3 py-4 text-center">
          <User className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <div className="text-sm text-gray-500">
            {searchQuery ? `No agents found for "${searchQuery}"` : 'No agents available'}
          </div>
        </div>
      )}

      {/* Agent list - Instagram style */}
      {!isLoading && filteredAgents.length > 0 && (
        <div className="py-1">
          {filteredAgents.map((agent, index) => (
            <div
              key={agent.id}
              className={cn(
                "px-4 py-3 cursor-pointer flex items-center gap-3 hover:bg-gray-50 transition-colors",
                index === highlightedIndex && "bg-blue-50"
              )}
              onClick={() => {
                logger.info('🎯 Agent selected for @mention', { 
                  agentId: agent.id, 
                  username: agent.username,
                  displayName: agent.display_name 
                })
                onSelect(agent)
              }}
            >
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <img
                  src={agent.avatar}
                  alt={agent.display_name}
                  className="w-10 h-10 rounded-full"
                />
                {/* Small online indicator */}
                {agent.is_online && (
                  <div className="absolute -bottom-0 -right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                )}
              </div>

              {/* Agent name - Instagram style */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {agent.display_name}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  @{agent.username}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer hint */}
      {!isLoading && filteredAgents.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-100">
          <div className="text-xs text-gray-400">
            ↑↓ to navigate • Enter to select
          </div>
        </div>
      )}
    </div>
  )
}